import { type NovelItem, NovelItem_Status } from '@app/_proto/Protos/novels';
import { SectionType } from '@app/_proto/Protos/sections';
import type { SponsorPlanItem } from '@app/_proto/Protos/sponsors';
import type { Timestamp } from '@app/_proto/google/protobuf/timestamp';
import { logSelectSeries } from '@app/analytics';
import Countdown from '@app/components/shared/Countdown';
import { ChampionPlanWithPriceButton } from '@app/domains/champion/components/ChampionPlanButtonOnly';
import ChampionPlans from '@app/domains/champion/components/ChampionPlans';
import ChampionPlanWithDialog from '@app/domains/champion/containers/ChampionPlanWithDialog';
import { championDiscountState } from '@app/domains/champion/utils/championDiscountState';
import { sortSponsorPlans } from '@app/domains/champion/utils/sortSponsorPlans';
import PureSkeleton from '@app/domains/common/components/PureSkeleton';
import { getNovelPath } from '@app/domains/common/utils/path';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import HeroTextPlaceholder from '@app/domains/novel/components/HeroTextPlaceholder';
import NovelResponsiveCoverImage from '@app/domains/novel/components/NovelResponsiveCoverImage';
import { BASE_SKELETON_PROPS } from '@app/domains/novel/constants';
import { convertNovelStatusToStr } from '@app/domains/novel/utils';
import { useAuth } from '@app/libs/auth';
import { breakpoints } from '@app/utils/breakpoints';
import { useMediaQuery } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

type Props = {
    novel: NovelItem | undefined;
    isLoading?: boolean;
};

export default function HomeSubscriptionsContent({ novel, isLoading }: Props) {
    return (
        <div className="w-full min-h-226 xs2:min-h-268 lg:min-h-280 bg-gray-100 dark:bg-black rounded-10 p-14 lg:p-24 flex flex-col">
            <HeroTextPlaceholder
                data-testid="skeleton"
                className="h-[17px] w-[200px] self-center"
                item={!isLoading}
                skeletonProps={BASE_SKELETON_PROPS}
            >
                <div className="line-clamp-1 break-word font-set-b15 text-center mt-5 text-black dark:text-white">
                    {novel?.name}
                </div>
            </HeroTextPlaceholder>
            <div className="flex-1 flex mt-5 items-center justify-center">
                <div className="hidden xs:block w-[80px] xs2:w-[140px] sm:w-[133px] mr-[8px]">
                    <HomeSubscriptionCover novel={novel} isLoading={isLoading} />
                </div>
                <div className="flex-1 flex h-full justify-center items-center max-w-400">
                    <HomeSubscriptionCard novel={novel} isLoading={isLoading} />
                </div>
            </div>
        </div>
    );
}

function HomeSubscriptionCover({ novel, isLoading }: Props) {
    const handleClickSeries = (novel: NovelItem | undefined) => {
        if (!novel) return;

        logSelectSeries(novel);
    };

    return (
        <Link
            onClick={() => handleClickSeries(novel)}
            to={getNovelPath(novel?.slug || '')}
            data-posthog-click-event={HomeEvents.ClickCollectionItem}
        >
            <div className={'relative h-0 pb-[146%]'}>
                <NovelResponsiveCoverImage
                    novel={novel}
                    imgUrl={novel?.coverUrl?.value}
                    imgBlurHash={novel?.coverBlurHash?.value}
                    isLoading={isLoading}
                    srcWidth={200}
                    srcHeight={100}
                    classes={{
                        bookCover: 'rounded-r-5',
                        img: 'rounded-r-5 drop-shadow-ww-home-content-image',
                    }}
                >
                    {novel?.status !== undefined && (
                        <div className="font-set-b9 bg-gray-950/50 absolute top-0 rounded-[0_0_4px_0] py-4 px-6 text-white">
                            {convertNovelStatusToStr(novel.status)}
                        </div>
                    )}
                </NovelResponsiveCoverImage>
            </div>
        </Link>
    );
}

function HomeSubscriptionCard({ novel, isLoading }: Props) {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const isSmallScreen = useMediaQuery(breakpoints.downXs2);

    const sortedSponsorPlans = useMemo(() => {
        if (!user) {
            return undefined;
        }

        return sortSponsorPlans(novel?.sponsorInfo?.plans);
    }, [user, novel?.sponsorInfo?.plans]);

    const viewData = useMemo(():
        | {
              view: 'champion-discount' | 'champion-novel-completed' | 'champion-without-discount' | undefined;
              plan: SponsorPlanItem;
              discountText?: string | undefined;
              endsAt?: Timestamp | undefined;
          }
        | undefined => {
        if (!novel || novel?.series?.relatedUserInfo?.isUnlocked) {
            return undefined;
        }

        const planWithDiscount = sortedSponsorPlans?.find(plan => plan.discountInfo?.discountedPrice !== undefined);
        if (planWithDiscount) {
            if (sortedSponsorPlans && sortedSponsorPlans.length > 0) {
                const discountState = championDiscountState(null, sortedSponsorPlans);
                if (discountState.plansWithDiscount && discountState.plansWithDiscount.length > 0) {
                    return {
                        view: 'champion-discount',
                        plan: discountState.plansWithDiscount[0],
                        discountText: discountState.discountText,
                        endsAt: discountState.endsAt,
                    };
                }
            }
        }

        if (sortedSponsorPlans && sortedSponsorPlans.length > 0) {
            return {
                view:
                    novel?.status === NovelItem_Status.Finished
                        ? 'champion-novel-completed'
                        : 'champion-without-discount',
                plan: sortedSponsorPlans[0],
            };
        }

        return undefined;
    }, [novel, novel?.series?.relatedUserInfo?.isUnlocked, sortedSponsorPlans, novel?.status]);

    const refetchData = useCallback(() => {
        queryClient.invalidateQueries(['sections', SectionType.NovelCTA]);
    }, [queryClient]);

    if (isLoading || !sortedSponsorPlans || viewData === undefined) {
        return (
            <PureSkeleton
                className="w-full h-[170px] lg:h-[211px] max-h-full rounded-8"
                variant="rectangular"
                animation="pulse"
            />
        );
    }

    return (
        <>
            {(viewData?.view === 'champion-discount' || viewData?.view === 'champion-without-discount') && (
                <div className="flex-1">
                    <div
                        className={clsx('', {
                            'bg-blue-600 rounded-t-8 rounded-b-12': !!viewData?.discountText,
                        })}
                    >
                        {viewData?.discountText && (
                            <div className="flex flex-row justify-between items-center gap-10 px-8 pt-8 pb-8 sm:pb-20">
                                <div className="uppercase bg-yellow-600 px-10 py-5 rounded-4 text-[10px] leading-[12.8px] xs2:text-[14px] xs2:leading-[16.8px] font-bold text-black">
                                    {viewData?.discountText} now!
                                </div>
                                {viewData?.endsAt && (
                                    <Countdown endsAt={viewData?.endsAt} countdownEnded={refetchData} />
                                )}
                            </div>
                        )}
                        <div>
                            <ChampionPlans
                                novelId={novel?.id}
                                sponsorPlans={[viewData.plan]}
                                currentSubscription={null}
                                gapX={16}
                                gapY={12}
                                maxColumn={1}
                                descLines={1}
                                showTierInfo={!isSmallScreen}
                            />
                        </div>
                    </div>
                </div>
            )}
            {viewData?.view === 'champion-novel-completed' && (
                <div className="flex-1 text-center xs:text-left">
                    <div className="font-bold text-[14px] lg:text-[22px] leading-tight mb-10">
                        <div className="text-black dark:text-white">
                            Subscribe to <span className="text-blue-600">Full Series Champion</span>
                        </div>
                    </div>
                    <div className="text-gray-750 dark:text-gray-400 leading-tight text-[12px] lg:text-[14px]">
                        Access <span className="font-bold">ALL CHAPTERS</span> and help support authors and translators.
                    </div>
                    <div className="mx-auto xs2:mx-0 xs2:max-w-355 mt-10">
                        <ChampionPlanWithDialog
                            plan={viewData.plan}
                            currentSubscription={null}
                            renderChampionPlan={ChampionPlanWithPriceButton}
                            onSubscribePlan={() => refetchData}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
